import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./CSE.module.css";
import { motion, AnimatePresence } from "framer-motion";

// Expect each file to export an array of question objects:
// e.g. export default [{ question: "...", options: [...], answer: "A", level: "easy" }, ...]
import cseData1 from "./CSEData1";
import cseData2 from "./CSEData2";

// Combine questions correctly
const allQuizData = [...(cseData1 || []), ...(cseData2 || [])];

// Function to get random N questions (default 12) with 4 easy and rest medium/hard when possible
const getRandomQuestions = (data, count = 12) => {
  const easy = data.filter((q) => q.level === "easy");
  const medium = data.filter((q) => q.level === "medium");
  const hard = data.filter((q) => q.level === "hard");

  const shuffle = (arr) => {
    // stable shuffle
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  };

  const selectCount = Math.min(count, data.length);
  const selectedEasy = shuffle(easy).slice(
    0,
    Math.min(4, selectedEasyLength(easy, selectCount))
  );
  const medHardPool = shuffle([...medium, ...hard]);
  const selectedMedHard = medHardPool.slice(
    0,
    selectCount - selectedEasy.length
  );

  return shuffle([...selectedEasy, ...selectedMedHard]);

  function selectedEasyLength(easyArr, totalCount) {
    // If not enough easy available, use as many as exist but never more than totalCount
    return Math.min(4, easyArr.length, totalCount);
  }
};

const CSE = () => {
  const navigate = useNavigate();

  // quiz & UI state
  const [quizData, setQuizData] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({}); // { [questionIndex]: selectedOption }
  const answersRef = useRef(answers); // keep a ref for timer closure safety
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(null);
  const [error, setError] = useState("");
  const [direction, setDirection] = useState("forward");
  const [timeLeft, setTimeLeft] = useState(10);
  const [showRules, setShowRules] = useState(true);
  const [disqualified, setDisqualified] = useState(false);

  const timerRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    // track mounted state to avoid setting state after unmount
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // Load quiz once when rules are closed
  useEffect(() => {
    if (!showRules && quizData.length === 0) {
      const loaded = getRandomQuestions(allQuizData, 12);
      setQuizData(loaded);
      setCurrentQuestion(0);
      setAnswers({});
      answersRef.current = {};
      setShowResults(false);
      setScore(null);
      setDisqualified(false);
      setError("");
      setTimeLeft(10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRules]); // intentionally only depends on showRules

  const totalQuestions = quizData.length;

  // timer effect: runs when currentQuestion or quizData changes
  useEffect(() => {
    // cleanup any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // nothing to do if quiz not loaded or already finished
    if (
      quizData.length === 0 ||
      currentQuestion >= totalQuestions ||
      showResults ||
      disqualified
    ) {
      return;
    }

    // reset timer for the newly active question
    setTimeLeft(10);

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const next = prev - 1;

        if (next <= 0) {
          // time is up for current question
          // check the most recent answers via answersRef
          const hasAnswered =
            answersRef.current.hasOwnProperty(currentQuestion);

          // clear interval now to avoid double runs
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }

          if (!hasAnswered) {
            // Disqualify user if they didn't answer in time
            if (isMounted.current) {
              setDisqualified(true);
            }
            return 0;
          } else {
            // If answered, move to next or submit
            if (currentQuestion < totalQuestions - 1) {
              if (isMounted.current) {
                setCurrentQuestion((prevQ) => prevQ + 1);
                setDirection("forward");
              }
            } else {
              // last question -> submit
              if (isMounted.current) {
                handleSubmit();
              }
            }
            return 0;
          }
        }

        return next;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, quizData, showResults, disqualified]);

  // keep answersRef in sync
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // option select
  const handleOptionSelect = (option) => {
    if (timeLeft <= 0 || disqualified || showResults) return;

    setAnswers((prev) => {
      const next = { ...prev, [currentQuestion]: option };
      answersRef.current = next;
      return next;
    });
    setError("");
  };

  // navigation
  const handleNext = () => {
    if (answers[currentQuestion] === undefined) {
      setError("Please select an answer before proceeding.");
      // small shake animation is handled by framer-motion in render
      return;
    }
    setError("");
    setDirection("forward");
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    // calculate score
    let finalScore = 0;
    for (let i = 0; i < totalQuestions; i++) {
      if (answers[i] !== undefined && answers[i] === quizData[i].answer) {
        finalScore++;
      }
    }
    setScore(finalScore);
    setShowResults(true);

    // cleanup timer if present
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleRestart = () => {
    // navigate to home (or restart quiz flow)
    // clear any timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    navigate("/");
  };

  const handleStartQuiz = () => setShowRules(false);

  // Animations
  const questionVariants = {
    hidden: (dir) => ({ opacity: 0, x: dir === "forward" ? 100 : -100 }),
    visible: { opacity: 1, x: 0, transition: { duration: 0.25 } },
    exit: (dir) => ({
      opacity: 0,
      x: dir === "forward" ? -100 : 100,
      transition: { duration: 0.25 },
    }),
  };
  const errorShake = {
    shake: { x: [0, -8, 8, -8, 8, 0], transition: { duration: 0.4 } },
  };

  // RULES screen
  if (showRules) {
    return (
      <div className={styles.quizContainer}>
        <div className={styles.rulesModal}>
          <h2 className={styles.rulesTitle}>📜 Quiz Rules</h2>
          <ul className={styles.rulesList}>
            <li>
              1️⃣ You have <b>10 seconds</b> to answer each question.
            </li>
            <li>2️⃣ You can change your answer within the time limit.</li>
            <li>3️⃣ All questions are mandatory.</li>
            <li>
              4️⃣ If you fail to answer within the time limit for any question,
              you will be <b>disqualified ❌</b>.
            </li>
            <li>5️⃣ Score 0–8: no prize. 9–10: Good. 11–12: Superb! 🏆</li>
          </ul>
          <button onClick={handleStartQuiz} className={styles.startButton}>
            Start Now 🚀
          </button>
        </div>
      </div>
    );
  }

  // Disqualified screen
  if (disqualified) {
    return (
      <div className={styles.quizContainer}>
        <motion.div
          className={styles.quizCard}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
        >
          <h2 className={styles.resultsTitle}>❌ You Are Disqualified</h2>
          <p className={styles.congratsText}>
            You didn’t answer in time. Please try again.
          </p>
          <button
            onClick={() => {
              if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
              }
              navigate("/");
            }}
            className={styles.submitButton}
            style={{ width: "100%", marginTop: "2rem" }}
          >
            Go to Home 🏠
          </button>
        </motion.div>
      </div>
    );
  }

  // Loading / wait for quizData
  if (quizData.length === 0) {
    return (
      <div className={styles.quizContainer}>
        <div className={styles.quizCard}>
          <div className={styles.loadingSpinner}>Loading quiz...</div>
        </div>
      </div>
    );
  }

  const progressPercent =
    totalQuestions > 0 ? ((currentQuestion + 1) / totalQuestions) * 100 : 0;

  return (
    <div className={styles.quizContainer}>
      <motion.div
        className={styles.quizCard}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <AnimatePresence mode="wait">
          {showResults ? (
            <motion.div
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={styles.resultsContainer}
            >
              <h2 className={styles.resultsTitle}>🎉 Quiz Results</h2>
              <p className={styles.congratsText}>You completed the quiz!</p>
              <div className={styles.codeDisplay}>
                <p className={styles.codeLabel}>Your Score:</p>
                <p className={styles.codeValue}>
                  {score}/{quizData.length}
                </p>
                <p className={styles.scoreMessage}>
                  {score <= 8 ? "Fair!" : score <= 10 ? "Good!" : "Superb!"}
                </p>
              </div>
              <button
                onClick={handleRestart}
                className={styles.submitButton}
                style={{ width: "100%", marginTop: "2rem" }}
              >
                Try Again
              </button>
            </motion.div>
          ) : (
            <motion.div key={`quiz-${currentQuestion}`}>
              {/* Progress Bar */}
              <div className={styles.progressBarContainer}>
                <motion.div
                  className={styles.progressBar}
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>

              {/* Header */}
              <div className={styles.questionHeader}>
                <div>
                  Question {currentQuestion + 1} of {totalQuestions}
                </div>
                <div className={styles.timer}>
                  Time left: <strong>{timeLeft}s</strong>
                </div>
              </div>

              {/* Question + Options */}
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentQuestion}
                  custom={direction}
                  variants={questionVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className={styles.questionWrapper}
                >
                  <h3 className={styles.questionText}>
                    {quizData[currentQuestion].question}
                  </h3>
                  <div className={styles.optionsContainer}>
                    {quizData[currentQuestion].options.map((option, index) => (
                      <button
                        key={`${currentQuestion}-${option}`}
                        className={`${styles.optionButton} ${
                          answers[currentQuestion] === option
                            ? styles.selectedOption
                            : ""
                        }`}
                        onClick={() => handleOptionSelect(option)}
                        disabled={timeLeft === 0}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Error */}
              <AnimatePresence>
                {error && (
                  <motion.p
                    className={styles.errorText}
                    variants={errorShake}
                    animate="shake"
                    key="error"
                  >
                    {error}
                  </motion.p>
                )}
              </AnimatePresence>

              {/* Navigation */}
              <div className={styles.navigationButtons}>
                {currentQuestion < totalQuestions - 1 ? (
                  <button onClick={handleNext} className={styles.submitButton}>
                    Next
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      // require selection on final question too
                      if (answers[currentQuestion] === undefined) {
                        setError("Please select an answer before submitting.");
                        return;
                      }
                      handleSubmit();
                    }}
                    className={styles.submitButton}
                  >
                    Submit Quiz
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default CSE;
